import React from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "@/lib/i18n";
import { StockailyWordmark } from "@/components/brand/StockailyBrand";
import CookieSettingsButton from "@/components/consent/CookieSettingsButton";

/**
 * Footer — premium, intentional, native fintech feel.
 *
 * Mobile rhythm:
 *   1. Cinematic top hairline + soft fade glow (signals "end of page")
 *   2. Brand block (bigger glyph, real tagline) + locale switcher
 *   3. Two compact link columns with proper UC eyebrows
 *   4. Quieter disclaimer (clearly distinct)
 *   5. Hairline rule + © year + built-for line
 *   6. Oversized closing wordmark stamp as visual closure
 */
export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer
      className="app-dark-surface relative mt-16 sm:mt-24 text-sm text-muted-foreground"
      data-testid="app-footer"
    >
      {/* top hairline accent */}
      <div className="absolute top-0 left-0 right-0 h-px"
           style={{ background: "linear-gradient(90deg, transparent, rgba(125,133,146,0.40), transparent)" }} />
      {/* very soft top glow — anchors the footer as a "closing room" */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 opacity-70"
           style={{ background: "radial-gradient(60% 100% at 50% 0%, rgba(49,130,206,0.06), transparent 70%)" }} />
      <div className="relative pt-10 sm:pt-12 pb-7 sm:pb-9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Brand block + columns */}
          <div className="md:grid md:grid-cols-12 md:gap-10">
            <div className="md:col-span-5">
              {/* Stockaily brand v1 — approved horizontal wordmark. Footer
                  is on the same dark surface as the navbar (.app-dark-surface),
                  so the dark-bg wordmark is safe here regardless of theme. */}
              <div className="flex items-center">
                <StockailyWordmark height={34} />
              </div>
              <div className="mt-4 max-w-sm">
                <p className="text-[13px] sm:text-[13px] leading-[1.6] text-muted-foreground/85">
                  {t("footer.tagline")}
                </p>
                {/* Social media — Instagram + X (post-launch footer update
                    2026-02-09). Monochrome, matches surrounding footer
                    tone; no tracking or analytics attached. */}
                <SocialLinks />
                <div className="mt-4 sm:hidden">
                  <LanguageSwitcher variant="navbar" />
                </div>
              </div>
            </div>

            {/* Links: 2-col on mobile, 4-col within the right span on md+ */}
            <div className="mt-9 md:mt-0 md:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
              <FooterCol title={t("footer.product")}>
                {/* Phase 8.LP.LR v1.8 — Product link order:
                    1) Analyze  2) Watchlist  3) Portfolio
                    4) Pricing  5) Log in. */}
                <FooterLink to="/analyze"   testId="footer-link-analyze">{t("footer.link_analyze")}</FooterLink>
                <FooterLink to="/watchlist" testId="footer-link-watchlist">{t("footer.link_watchlist")}</FooterLink>
                <FooterLink to="/portfolio" testId="footer-link-portfolio">{t("footer.link_portfolio")}</FooterLink>
                <FooterLink to="/pricing"   testId="footer-link-pricing">{t("footer.link_pricing")}</FooterLink>
                <FooterLink to="/login"     testId="footer-link-login">{t("footer.link_login")}</FooterLink>
              </FooterCol>
              {/* WS-11 Final Launch Reconciliation — legal links split
                  into two compact columns, one per language. Legacy
                  `/legal/*` short-URLs (Privacy, Terms, Cookies,
                  Impressum) redirect to the canonical DE/EN routes,
                  so we no longer render them separately in the
                  footer — that removes the previous duplicate first
                  row. */}
              <FooterCol title="Deutsch">
                <FooterLink to="/de/impressum"          testId="footer-link-ws11-de-impressum">Impressum</FooterLink>
                <FooterLink to="/de/datenschutz"        testId="footer-link-ws11-de-datenschutz">Datenschutz</FooterLink>
                <FooterLink to="/de/agb"                testId="footer-link-ws11-de-agb">AGB</FooterLink>
                <FooterLink to="/de/widerruf"           testId="footer-link-ws11-de-widerruf">Widerruf</FooterLink>
                <FooterLink to="/de/widerrufsformular"  testId="footer-link-ws11-de-widerrufsformular">Widerrufsformular</FooterLink>
                <FooterLink to="/de/disclaimer"         testId="footer-link-ws11-de-disclaimer">Disclaimer</FooterLink>
                <FooterLink to="/de/cookies"            testId="footer-link-ws11-de-cookies">Cookies</FooterLink>
                <FooterLink to="/de/research-transparenz" testId="footer-link-de-research-transparency">Research-Transparenz</FooterLink>
              </FooterCol>
              <FooterCol title="English">
                <FooterLink to="/en/legal-notice"       testId="footer-link-ws11-en-legal-notice">Legal Notice</FooterLink>
                <FooterLink to="/en/privacy"            testId="footer-link-ws11-en-privacy">Privacy Policy</FooterLink>
                <FooterLink to="/en/terms"              testId="footer-link-ws11-en-terms">Terms</FooterLink>
                <FooterLink to="/en/withdrawal"         testId="footer-link-ws11-en-withdrawal">Withdrawal</FooterLink>
                <FooterLink to="/en/withdrawal-form"    testId="footer-link-ws11-en-withdrawal-form">Withdrawal Form</FooterLink>
                <FooterLink to="/en/disclaimer"         testId="footer-link-ws11-en-disclaimer">Financial Disclaimer</FooterLink>
                <FooterLink to="/en/cookies"            testId="footer-link-ws11-en-cookies">Cookie Policy</FooterLink>
                <FooterLink to="/en/research-transparency" testId="footer-link-en-research-transparency">Research Transparency</FooterLink>
              </FooterCol>
              <div className="col-span-2 md:col-span-1">
                <div className="text-[10px] font-semibold uppercase tracking-[0.20em] text-muted-foreground/85">{t("footer.disclaimer")}</div>
                <p data-testid="footer-legal-disclaimer" className="mt-3 text-[11px] sm:text-[11.5px] leading-[1.6] text-muted-foreground/55">
                  {t("footer.disclaimer_body")}
                </p>
              </div>
            </div>
          </div>

          {/* Bottom row */}
          <div className="mt-9 sm:mt-12 pt-5 border-t border-border/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[10.5px] tracking-wide text-muted-foreground/55">
            <span>{t("footer.rights", { year: new Date().getFullYear() })}</span>
            <div className="flex items-center gap-4">
              {/* Consent Foundation (2026-08-02) — permanent bilingual
                  cookie-settings control.  Opens the settings dialog
                  without a page reload and remains available after
                  every decision type.
                  WS-4 STAGING FIX (2026-08-06) — do NOT override the
                  button's own className here.  The previous override
                  reduced the control to `text-[10.5px]` at 55%
                  muted-foreground opacity, making it visually
                  indistinguishable from the copyright text on the
                  same row and therefore not identifiable as a
                  control.  Letting the component render with its own
                  defaults produces the underlined link-button
                  affordance the spec calls for. */}
              <CookieSettingsButton />
              <span>{t("footer.built_for")}</span>
            </div>
          </div>

          {/* Closing wordmark — quiet, oversized, expensive-feeling */}
          <div
            aria-hidden
            className="mt-8 sm:mt-10 select-none text-center font-display tracking-[0.45em] text-[11px] sm:text-[13px] uppercase text-muted-foreground/15"
          >
            S T O C K A I L Y
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.20em] text-muted-foreground/85">{title}</div>
      <ul className="mt-3.5 space-y-2 text-[13px]">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children, testId }) {
  return (
    <li>
      <Link
        className="text-muted-foreground/85 hover:text-foreground transition-colors"
        to={to}
        data-testid={testId}
      >
        {children}
      </Link>
    </li>
  );
}

/**
 * Post-launch footer update (2026-02-09) — social-media icon links.
 *
 * Renders compact monochrome icon links for Instagram and X sitting
 * just below the Stockaily tagline in the brand block.  Icons inherit
 * the surrounding muted-foreground palette (`/85 → /100 on hover`) so
 * they match the existing footer aesthetic exactly.  Both links open
 * in a new tab and carry `rel="noopener noreferrer"`.
 *
 * Instagram uses the `lucide-react` glyph already used across the app.
 * X uses an inline SVG of the current X monochrome glyph (not the
 * former Twitter bird) so we don't ship a stale mark.
 */
function SocialLinks() {
  const linkClass =
    "inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground/85 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60";
  return (
    <div className="mt-4 flex items-center gap-2" data-testid="footer-social-links">
      <a
        href="https://www.instagram.com/stockaily/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Stockaily on Instagram"
        className={linkClass}
        data-testid="footer-social-instagram"
      >
        <Instagram className="h-4 w-4" aria-hidden="true" />
      </a>
      <a
        href="https://x.com/stockaily"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Stockaily on X"
        className={linkClass}
        data-testid="footer-social-x"
      >
        <XLogo />
      </a>
    </div>
  );
}

/**
 * Current X (formerly Twitter) monochrome glyph.  Rendered inline so
 * we don't rely on the outdated `Twitter` bird glyph in lucide-react
 * or ship an extra icon library.  Uses `currentColor` so it inherits
 * the surrounding footer palette / hover state.
 */
function XLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M18.244 2H21.5l-7.53 8.61L23 22h-6.828l-5.34-6.98L4.7 22H1.44l8.052-9.207L1 2h6.914l4.83 6.39L18.244 2Zm-1.197 18h1.884L7.03 4H5.014l12.033 16Z" />
    </svg>
  );
}
